import { useState } from "react";
import { motion } from "framer-motion";
import { FileText, ArrowUpRight } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { SERVICE_KEYS, SERVICE_IMG } from "@/content";
import { Reveal, Eyebrow, SectionTitle, EASE } from "./Reveal";
import { SpecSheet } from "./SpecSheet";

const SECTORS = { cctv: ["residential", "commercial", "industrial"], alarm: ["residential", "commercial", "industrial"], access: ["residential", "commercial", "industrial"], fire: ["commercial", "industrial"], time: ["commercial", "industrial"] };

const TabList = ({ active, setActive, items }) => (
  <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible -mx-5 px-5 lg:mx-0 lg:px-0 pb-2 lg:pb-0" role="tablist">
    {SERVICE_KEYS.map((k, i) => (
      <button
        key={k} role="tab" aria-selected={active === k} data-testid={`service-tab-${k}`} onClick={() => setActive(k)}
        className={`group relative shrink-0 text-left px-5 py-4 lg:py-5 border transition-colors duration-300 ${active === k ? "border-red-600/60 bg-red-950/30" : "border-white/10 hover:border-white/30 bg-white/[0.02]"}`}
      >
        {active === k && <motion.span layoutId="svc-bar" className="absolute left-0 top-0 bottom-0 w-[3px] bg-red-600 red-glow" />}
        <span className="font-mono text-xs text-red-500 mr-3">0{i + 1}</span>
        <span className={`font-display font-bold uppercase text-xl lg:text-2xl tracking-wide transition-colors ${active === k ? "text-white" : "text-zinc-400 group-hover:text-white"}`}>{items[k].short}</span>
      </button>
    ))}
  </div>
);

const Panel = ({ k, s, t, onSheet }) => (
  <motion.div key={k} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="grid md:grid-cols-2 gap-8 lg:gap-10" data-testid={`service-panel-${k}`}>
    <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[520px] clip-tac overflow-hidden bg-zinc-900 group">
      <motion.img key={SERVICE_IMG[k]} src={SERVICE_IMG[k]} alt={s.title} initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: EASE }} className="absolute inset-0 h-full w-full object-cover grayscale contrast-125 brightness-75 group-hover:grayscale-0 transition-[filter] duration-700" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,transparent_20%,rgba(5,5,5,0.85)_75%)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-red-950/60 to-transparent mix-blend-multiply" />
      <span className="absolute bottom-5 left-6 font-mono text-xs tracking-[0.25em] text-zinc-200">SYS / {k.toUpperCase()}</span>
    </div>
    <div className="flex flex-col">
      <h3 className="font-display font-extrabold uppercase text-3xl sm:text-4xl text-white leading-none" data-testid="service-panel-title">{s.title}</h3>
      <p className="mt-5 text-zinc-300 leading-relaxed">{s.desc}</p>
      <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{t.specs}</p>
      <dl className="mt-3 border-t border-white/10">
        {s.specs.map(([a, b], i) => (
          <motion.div key={a} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.06 }} className="flex justify-between gap-6 py-3 border-b border-white/10">
            <dt className="text-sm text-zinc-400">{a}</dt>
            <dd className="text-sm text-white text-right font-medium">{b}</dd>
          </motion.div>
        ))}
      </dl>
      <div className="mt-6 flex flex-wrap gap-2">
        {SECTORS[k].map((sec) => (
          <span key={sec} className="font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border border-white/15 text-zinc-300">{t.sectorNames[sec]}</span>
        ))}
      </div>
      <div className="mt-auto pt-8 flex flex-col sm:flex-row gap-3">
        <button data-testid={`service-sheet-button-${k}`} onClick={onSheet} className="clip-btn flex items-center justify-center gap-2 border border-white/20 hover:border-red-500 hover:text-red-400 transition-colors px-5 py-3.5 font-mono text-xs uppercase tracking-[0.16em] text-white"><FileText className="h-4 w-4" />{t.sheet}</button>
        <button data-testid={`service-book-button-${k}`} onClick={() => scrollToId("booking")} className="group clip-btn flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 transition-colors px-5 py-3.5 font-mono text-xs uppercase tracking-[0.16em] text-white">{t.book}<ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></button>
      </div>
    </div>
  </motion.div>
);

export const Services = () => {
  const { t } = useLang();
  const [active, setActive] = useState("cctv");
  const [sheet, setSheet] = useState(false);
  const s = t.services;
  return (
    <section id="services" data-testid="services-section" className="relative py-28 lg:py-40">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <Reveal className="lg:col-span-7"><Eyebrow>{s.eyebrow}</Eyebrow><SectionTitle className="mt-6">{s.title}</SectionTitle></Reveal>
          <Reveal delay={0.1} className="lg:col-span-5"><p className="text-zinc-300 text-base md:text-lg leading-relaxed">{s.sub}</p></Reveal>
        </div>
        <Reveal delay={0.1} className="mt-16 grid lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-3"><TabList active={active} setActive={setActive} items={s.items} /></div>
          <div className="lg:col-span-9"><Panel k={active} s={s.items[active]} t={s} onSheet={() => setSheet(true)} /></div>
        </Reveal>
      </div>
      <SpecSheet open={sheet} onOpenChange={setSheet} k={active} />
    </section>
  );
};
